import { useMemo } from 'react';
import {
  MessageType,
  TaskState,
  MessageSkill,
  Message,
  isImageGenerationCard,
  isChitChatCard,
  TaskSkill,
} from 'src/types';
import { useComparingConversationsParams, useGetUser } from 'src/hooks';
import styles from './MessageActions.module.scss';
import { RegenerateMessageControl } from 'src/v2/components/ThreadMessage/components/RegenerateMessageControl';
import { RephraseMessageButton } from '../RephraseMessageButton';
import { TaskDetailsButton } from '../TaskDetailsButton';
import { DeleteMessageButton } from '../DeleteMessageButton';
import { CopyMessageButton } from '../CopyMessageButton';
import { ShareButton } from 'src/components/ShareButton';
import { useChatHistory } from 'src/hooks';

export const SKILLS_WITH_REDO: Array<MessageSkill> = [
  MessageSkill.WRITER,
  MessageSkill.CODER,
  MessageSkill.DEEP_RESEARCHER,
  MessageSkill.RESEARCHER,
  MessageSkill.IMAGE_GENERATION,
  MessageSkill.VIDEO_GENERATION,
];

interface MessageActionsProps {
  message: Message;
  taskId?: string;
  isStreaming: boolean;
  taskState?: TaskState;
  messageSkill: MessageSkill | null;
  activeTab: string;
}

export const MessageActions = ({
  message,
  taskId,
  isStreaming,
  taskState,
  messageSkill,
  activeTab,
}: MessageActionsProps) => {
  const { isNinjaRetired } = useChatHistory();

  const { isCompareMode } = useComparingConversationsParams();

  const { isOpenTier, isFreeTier } = useGetUser();

  const {
    message_id,
    payload,
    message_type,
    original_query,
    clean_query,
    refined_query,
    content,
  } = message;

  const isErrorMessage = useMemo(
    () => message_type === MessageType.ERROR_MESSAGE,
    [message_type],
  );

  const isRedoDisabled = useMemo(
    () =>
      !!(
        isStreaming ||
        (messageSkill && !SKILLS_WITH_REDO.includes(messageSkill))
      ),
    [isStreaming, messageSkill],
  );

  const shouldEnableActions = useMemo(() => {
    if (
      (isOpenTier || isFreeTier) &&
      isChitChatCard(payload) &&
      payload.data?.skill === TaskSkill.DOCUMENT_QA
    ) {
      return false;
    }
    if (isImageGenerationCard(payload) && (isOpenTier || isFreeTier)) {
      return false;
    }

    // TODO(olha): temporary
    if (message_type === MessageType.VIDEO_CARD) {
      return false;
    }

    if (
      message_type === MessageType.ERROR_MESSAGE ||
      message_type === MessageType.SCHEDULER_TASK_CREATION_CARD
    ) {
      return false;
    }

    if (message_type === MessageType.CONVERSATION && !isStreaming) {
      return true;
    }

    if (taskState === TaskState.DONE && !isStreaming) {
      return true;
    }

    if (!taskId && !isStreaming) {
      return true;
    }

    return false;
  }, [
    message_type,
    payload,
    isStreaming,
    taskState,
    taskId,
    isOpenTier,
    isFreeTier,
  ]);

  if (!message_id || isCompareMode) {
    return null;
  }

  return (
    <div className={styles.root}>
      {!isErrorMessage && (
        <>
          {!isNinjaRetired && (
            <TaskDetailsButton
              messageId={message_id}
              taskId={taskId}
              taskState={taskState}
              messageType={message_type}
              payload={payload}
              content={content}
            />
          )}

          {!isStreaming && (
            <>
              {!isNinjaRetired && (
                <RegenerateMessageControl
                  messageId={message_id}
                  disabled={isRedoDisabled}
                  messageType={message_type}
                  refinedQuery={refined_query}
                  cleanQuery={clean_query}
                  messageSkill={messageSkill}
                />
              )}

              {!isNinjaRetired && (
                <RephraseMessageButton originalQuery={original_query} />
              )}

              <CopyMessageButton
                activeTab={activeTab}
                message={message}
                disabled={
                  !shouldEnableActions ||
                  message_type === MessageType.IMAGE_CARD ||
                  message_type === MessageType.VIDEO_CARD
                }
              />

              {!isNinjaRetired && (
                <ShareButton
                  messageId={message_id}
                  disabled={!shouldEnableActions}
                />
              )}
            </>
          )}
        </>
      )}

      {!isNinjaRetired && !isStreaming && (
        <DeleteMessageButton message={message} disabled={isStreaming} />
      )}
    </div>
  );
};
